import { useEffect, useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Button, Card, Input } from '@/shared/ui';
import { getDB } from '@/shared/storage';
import { useReplicateClient } from '@/shared/hooks/useReplicateClient';
import { useSettings } from '@/shared/hooks/useSettings';
import { BUILT_IN_TEMPLATES } from '@/shared/catalog/templates';
import {
  IMAGE_MODELS,
  LLM_MODELS,
  imageModelsSupportingEdit,
  imageModelSupportsEdit,
} from '@/shared/catalog/models';
import { newId } from '@/shared/lib/id';
import { estimateJobCost, formatUsd } from '@/shared/pricing';
import { BrandDNASchema, type BrandDNA } from '@/entities/brand';
import type { Job } from '@/entities/job';
import type { Template } from '@/entities/template';
import { ASPECT_RATIOS, type AspectRatio } from '@/entities/aspect-ratio';
import { ResearchJobOutputSchema } from '@/entities/jobs/research';
import { isTerminalJobStatus } from '@/entities/job';
import { jobScheduler, useJob } from '@/shared/jobs';
import { createJobs, planResearchOnly, planSingleTemplate } from '@/features/image-generation/plan';
import { fetchBrandSnippet } from './researchPrompt';
import { BrandDnaForm } from './BrandDnaForm';

export function BrandDetailPage() {
  const params = useParams({ from: '/brands/$brandId' });
  const navigate = useNavigate();
  const [settings] = useSettings();
  const client = useReplicateClient();

  const brand = useLiveQuery(() => getDB().brands.get(params.brandId), [params.brandId]);
  const productImages = useLiveQuery(
    () => getDB().productImages.where('brandId').equals(params.brandId).sortBy('createdAt'),
    [params.brandId],
  );
  const brandJobs = useLiveQuery(
    () =>
      getDB()
        .jobs.where('brandId')
        .equals(params.brandId)
        .toArray()
        .then((js) => js.sort((a, b) => b.createdAt - a.createdAt)),
    [params.brandId],
  );

  if (!brand) return <p className="text-sm text-muted-foreground">Loading brand…</p>;

  const applyToBrandResearch = (brandJobs ?? []).filter(
    (j) =>
      j.kind === 'research' &&
      (j.input as { applyToBrand?: boolean } | null)?.applyToBrand === true,
  );
  const activeResearch = applyToBrandResearch.find(
    (j) => j.status === 'pending' || j.status === 'running',
  );
  const lastFailedResearch = applyToBrandResearch.find(
    (j) => j.status === 'failed' || j.status === 'canceled' || j.status === 'interrupted',
  );
  const stillEmpty =
    brand.dna.productDescription === '' &&
    brand.dna.usp === '' &&
    brand.dna.audience === '';
  const showFailureNotice = !activeResearch && lastFailedResearch && stillEmpty;

  return (
    <div className="space-y-6">
      <BrandHeader
        brandId={brand.id}
        name={brand.name}
        url={brand.url}
        onDeleted={() => void navigate({ to: '/brands' })}
      />
      {activeResearch ? (
        <Card className="space-y-2">
          <h2 className="font-medium">Researching brand DNA…</h2>
          <p className="text-sm text-muted-foreground">
            {activeResearch.label}. This brand will become editable once research finishes.
          </p>
        </Card>
      ) : (
        <>
          {showFailureNotice ? (
            <Card className="space-y-1 border-destructive/40">
              <h2 className="font-medium text-destructive">Research failed</h2>
              <p className="text-sm text-muted-foreground">
                {lastFailedResearch?.error ?? 'The research job did not complete.'} You can fill the
                DNA manually below or use Re-research to try again.
              </p>
            </Card>
          ) : null}
          <DnaCard
            brandId={brand.id}
            dna={brand.dna}
            brandName={brand.name}
            brandUrl={brand.url}
            autoEdit={Boolean(showFailureNotice)}
          />
          <ProductImagesCard
            brandId={brand.id}
            images={productImages ?? []}
            productType={brand.dna.productType}
          />
          <GenerateCard
            brand={{ id: brand.id, name: brand.name, dna: brand.dna }}
            productImages={productImages ?? []}
            defaults={{
              llmModelId: settings.defaultLlmModel,
              imageModelId: settings.defaultImageModel,
              resolution: settings.defaultImageResolutionPx,
              numImages: settings.defaultNumImagesPerTemplate,
            }}
            clientReady={Boolean(client)}
          />
          <ResultsCard brandId={brand.id} jobs={brandJobs ?? []} />
        </>
      )}
    </div>
  );
}

// ---------------- Header ----------------

function BrandHeader({
  brandId,
  name,
  url,
  onDeleted,
}: {
  brandId: string;
  name: string;
  url: string | undefined;
  onDeleted: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(name);
  const [draftUrl, setDraftUrl] = useState(url ?? '');

  async function save() {
    if (!draftName.trim()) return;
    await getDB().brands.update(brandId, {
      name: draftName.trim(),
      url: draftUrl.trim() || undefined,
      updatedAt: Date.now(),
    });
    setEditing(false);
  }

  async function deleteBrand() {
    if (!confirm('Delete this brand? Generated images, jobs and product images for this brand will also be removed.')) {
      return;
    }
    const db = getDB();
    const inFlight = await db.jobs
      .where('brandId')
      .equals(brandId)
      .filter((j) => !isTerminalJobStatus(j.status))
      .toArray();
    await Promise.all(inFlight.map((j) => jobScheduler.cancel(j.id)));
    await db.transaction(
      'rw',
      [db.brands, db.jobs, db.productImages, db.generatedImages, db.resolvedPrompts],
      async () => {
        const jobs = await db.jobs.where('brandId').equals(brandId).toArray();
        const jobIds = jobs.map((j) => j.id);
        if (jobIds.length > 0) {
          await db.generatedImages.where('jobId').anyOf(jobIds).delete();
          await db.resolvedPrompts.where('jobId').anyOf(jobIds).delete();
          await db.jobs.bulkDelete(jobIds);
        }
        await db.productImages.where('brandId').equals(brandId).delete();
        await db.brands.delete(brandId);
      },
    );
    onDeleted();
  }

  if (editing) {
    return (
      <Card className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Name">
            <Input value={draftName} onChange={(e) => setDraftName(e.target.value)} />
          </Field>
          <Field label="URL (optional)">
            <Input value={draftUrl} onChange={(e) => setDraftUrl(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={() => {
              setEditing(false);
              setDraftName(name);
              setDraftUrl(url ?? '');
            }}
          >
            Cancel
          </Button>
          <Button onClick={() => void save()}>Save</Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="flex items-start justify-between gap-2">
      <div>
        <h1 className="text-2xl font-semibold">{name}</h1>
        {url ? <p className="text-sm text-muted-foreground">{url}</p> : null}
      </div>
      <div className="flex gap-2">
        <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
          Edit
        </Button>
        <Button variant="destructive" size="sm" onClick={() => void deleteBrand()}>
          Delete
        </Button>
      </div>
    </div>
  );
}

// ---------------- DNA Card ----------------

function DnaCard({
  brandId,
  brandName,
  brandUrl,
  dna,
  autoEdit = false,
}: {
  brandId: string;
  brandName: string;
  brandUrl: string | undefined;
  dna: BrandDNA;
  autoEdit?: boolean;
}) {
  const [editing, setEditing] = useState(autoEdit);
  const [draft, setDraft] = useState<BrandDNA>(dna);
  const [reResearchOpen, setReResearchOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) setDraft(dna);
  }, [dna, editing]);

  async function save() {
    setError(null);
    const parsed = BrandDNASchema.safeParse(draft);
    if (!parsed.success) {
      setError(parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; '));
      return;
    }
    await getDB().brands.update(brandId, { dna: parsed.data, updatedAt: Date.now() });
    setEditing(false);
  }

  return (
    <Card className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-medium">Brand DNA</h2>
        {!editing ? (
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setReResearchOpen(true)}>
              Re-research
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
              Edit
            </Button>
          </div>
        ) : null}
      </div>

      {editing ? (
        <>
          <BrandDnaForm value={draft} onChange={setDraft} />
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setEditing(false);
                setDraft(dna);
                setError(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={() => void save()}>Save DNA</Button>
          </div>
        </>
      ) : (
        <DnaSummary dna={dna} />
      )}

      {reResearchOpen ? (
        <ReResearchDialog
          brandId={brandId}
          brandName={brandName}
          defaultUrl={brandUrl}
          onClose={() => setReResearchOpen(false)}
          onResult={(d) => {
            setDraft(d);
            setEditing(true);
            setReResearchOpen(false);
          }}
        />
      ) : null}
    </Card>
  );
}

function DnaSummary({ dna }: { dna: BrandDNA }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="space-y-2">
        <h3 className="text-xs font-semibold uppercase text-muted-foreground">Type</h3>
        <Kv k="Product type" v={dna.productType} />
        <h3 className="mt-3 text-xs font-semibold uppercase text-muted-foreground">Palette</h3>
        <div className="flex flex-wrap gap-2">
          {(['primaryColor', 'accentColor', 'background'] as const).map((k) => (
            <Swatch key={k} label={k.replace('Color', '')} value={dna[k]} />
          ))}
        </div>
        <h3 className="mt-3 text-xs font-semibold uppercase text-muted-foreground">Typography</h3>
        <Kv k="Typography" v={dna.typography} />
        <Kv k="Tone" v={dna.headlineTone} />
      </div>
      <div className="space-y-2">
        <h3 className="text-xs font-semibold uppercase text-muted-foreground">Brand essence</h3>
        <Kv k="Product" v={dna.productDescription} />
        <Kv k="USP" v={dna.usp} />
        <Kv k="Audience" v={dna.audience} />
        <Kv k="Photography" v={`${dna.photographyStyle} · ${dna.photographyMood}`} />
      </div>
      <div className="sm:col-span-2">
        <h3 className="text-xs font-semibold uppercase text-muted-foreground">Prompt modifier</h3>
        <p className="mt-1 whitespace-pre-wrap rounded bg-background/60 p-2 text-xs">
          {dna.promptModifier || <span className="text-muted-foreground">(empty)</span>}
        </p>
      </div>
    </div>
  );
}

function Swatch({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-2 rounded border border-border bg-background/40 px-2 py-1 text-xs">
      <span
        className="inline-block h-4 w-4 rounded border border-border"
        style={{ backgroundColor: value }}
      />
      <span className="capitalize text-muted-foreground">{label}:</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}

function Kv({ k, v }: { k: string; v: string }) {
  return (
    <div className="text-sm">
      <span className="text-xs uppercase text-muted-foreground">{k}: </span>
      <span>{v || <span className="text-muted-foreground">n/a</span>}</span>
    </div>
  );
}

// ---------------- Re-research dialog ----------------

function ReResearchDialog({
  brandId,
  brandName,
  defaultUrl,
  onClose,
  onResult,
}: {
  brandId: string;
  brandName: string;
  defaultUrl: string | undefined;
  onClose: () => void;
  onResult: (dna: BrandDNA) => void;
}) {
  const [settings] = useSettings();
  const client = useReplicateClient();
  const [urls, setUrls] = useState(defaultUrl ?? '');
  const [llmModelId, setLlmModelId] = useState(settings.defaultLlmModel);
  const [jobId, setJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const job = useJob(jobId ?? undefined);

  useEffect(() => {
    if (!job || job.status !== 'succeeded') return;
    const parsed = ResearchJobOutputSchema.safeParse(job.output);
    if (!parsed.success) {
      setError('Research succeeded but output was malformed.');
      return;
    }
    onResult(parsed.data.dna);
  }, [job, onResult]);

  async function start() {
    if (!client) {
      setError('Set your Replicate token in Settings first.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const list = urls
        .split(/[,\n]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const snippets = await Promise.all(list.map((u) => fetchBrandSnippet(u)));
      const combined = list
        .map((u, i) => `URL: ${u}\n${snippets[i] ?? ''}`)
        .join('\n\n---\n\n');
      const specs = planResearchOnly({
        brandName,
        brandUrl: list[0] ?? '',
        scrapedSnippet: combined,
        llmModelId,
        brandId,
      });
      const [rootId] = await createJobs(specs);
      setJobId(rootId ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start research.');
    } finally {
      setBusy(false);
    }
  }

  const running = job?.status === 'running' || job?.status === 'pending';

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-lg space-y-3 bg-background">
        <div className="flex items-center justify-between">
          <h3 className="font-medium">Re-research brand DNA</h3>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={running}>
            Close
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          One URL per line, or comma-separated. Result populates the DNA editor; your existing DNA stays untouched until you click Save.
        </p>
        <Field label="URLs">
          <textarea
            rows={3}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            value={urls}
            onChange={(e) => setUrls(e.target.value)}
          />
        </Field>
        <Field label="LLM">
          <select
            className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm"
            value={llmModelId}
            onChange={(e) => setLlmModelId(e.target.value)}
          >
            {LLM_MODELS.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
        </Field>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        {job?.status === 'failed' && job.error ? (
          <p className="text-sm text-destructive">Failed: {job.error}</p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={running}>
            Cancel
          </Button>
          <Button
            onClick={() => void start()}
            disabled={!urls.trim() || busy || running || !client}
          >
            {running ? 'Researching…' : 'Start research'}
          </Button>
        </div>
      </Card>
    </div>
  );
}

// ---------------- Product images ----------------

function ProductImagesCard({
  brandId,
  images,
  productType,
}: {
  brandId: string;
  images: readonly { id: string; filename: string; blob: Blob }[];
  productType: BrandDNA['productType'];
}) {
  const heading =
    productType === 'app'
      ? 'App screenshots'
      : productType === 'service'
        ? 'Reference images (optional)'
        : 'Product images';
  const emptyHelp =
    productType === 'app'
      ? 'No screenshots yet. Upload clean exports of your hero screens; they will be composited pixel-accurately into ads (UGC Hand-Held, Product Hero, etc.).'
      : productType === 'service'
        ? 'No images yet. Service brands rarely need them; most templates work without uploads.'
        : 'No product images yet. Upload clean product shots so templates like UGC Hand-Held or Product Hero can reference them.';
  const previews = useMemo(
    () => images.map((img) => ({ ...img, url: URL.createObjectURL(img.blob) })),
    [images],
  );
  useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)), [previews]);

  return (
    <Card className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-medium">{heading}</h2>
        <label className="cursor-pointer text-sm text-primary hover:underline">
          Upload
          <input
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              const now = Date.now();
              void getDB().productImages.bulkAdd(
                files.map((f) => ({
                  id: newId('img'),
                  brandId,
                  filename: f.name,
                  blob: f,
                  createdAt: now,
                })),
              );
              e.target.value = '';
            }}
          />
        </label>
      </div>
      {previews.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyHelp}</p>
      ) : (
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
          {previews.map((p) => (
            <div key={p.id} className="group relative">
              <img
                src={p.url}
                alt={p.filename}
                className="aspect-square w-full rounded border border-border object-cover"
                loading="lazy"
              />
              <button
                type="button"
                onClick={() => void getDB().productImages.delete(p.id)}
                className="absolute right-1 top-1 rounded bg-black/60 px-2 py-0.5 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100"
              >
                Delete
              </button>
              <div className="mt-1 truncate text-xs text-muted-foreground">{p.filename}</div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ---------------- Generate card ----------------

interface GenerateCardProps {
  brand: { id: string; name: string; dna: BrandDNA };
  productImages: readonly { id: string; filename: string; blob: Blob }[];
  defaults: {
    llmModelId: string;
    imageModelId: string;
    resolution: 1024 | 2048 | 4096;
    numImages: number;
  };
  clientReady: boolean;
}

function GenerateCard({ brand, productImages, defaults, clientReady }: GenerateCardProps) {
  const [template, setTemplate] = useState<Template | null>(null);
  const [llmModelId, setLlmModelId] = useState(defaults.llmModelId);
  const [imageModelId, setImageModelId] = useState(defaults.imageModelId);
  const [resolution, setResolution] = useState<1024 | 2048 | 4096>(defaults.resolution);
  const [numImages, setNumImages] = useState(defaults.numImages);
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('1:1');
  const [conceptBrief, setConceptBrief] = useState('');
  const [selectedImageIds, setSelectedImageIds] = useState<Set<string>>(
    new Set(productImages.map((p) => p.id)),
  );
  const [error, setError] = useState<string | null>(null);

  // When product images list changes, default-select all of them.
  useEffect(() => {
    setSelectedImageIds(new Set(productImages.map((p) => p.id)));
  }, [productImages]);

  // When a new template is picked, reset aspect ratio to the template's preset.
  useEffect(() => {
    if (template) setAspectRatio(template.aspectRatio);
  }, [template]);

  // When the chosen template requires product images and the current model is t2i-only,
  // auto-switch to a compatible model.
  useEffect(() => {
    if (!template?.needsProductImages) return;
    if (!imageModelSupportsEdit(imageModelId)) {
      const fallback = imageModelsSupportingEdit()[0];
      if (fallback) setImageModelId(fallback.id);
    }
  }, [template, imageModelId]);

  const availableImageModels = template?.needsProductImages
    ? imageModelsSupportingEdit()
    : IMAGE_MODELS;

  const previews = useMemo(
    () => productImages.map((img) => ({ ...img, url: URL.createObjectURL(img.blob) })),
    [productImages],
  );
  useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)), [previews]);

  const estimate = template
    ? estimateJobCost({
        llmModelId,
        imageModelId,
        templateCount: 1,
        numImagesPerTemplate: numImages,
        llmCallsCount: 1,
      })
    : null;

  const needsImagesButNoneSelected =
    template?.needsProductImages &&
    (selectedImageIds.size === 0 || productImages.length === 0);

  async function start() {
    if (!template) return;
    if (!clientReady) {
      setError('Set your Replicate token in Settings first.');
      return;
    }
    setError(null);
    const productImageIds = template.needsProductImages
      ? Array.from(selectedImageIds)
      : undefined;
    const specs = planSingleTemplate({
      brand: { id: brand.id, name: brand.name },
      template: { ...template, aspectRatio },
      dna: brand.dna,
      llmModelId,
      imageModelId,
      imageResolutionPx: resolution,
      numImagesPerTemplate: numImages,
      ...(productImageIds && productImageIds.length > 0 ? { productImageIds } : {}),
      ...(conceptBrief.trim().length > 0 ? { conceptBrief: conceptBrief.trim() } : {}),
    });
    await createJobs(specs);
  }

  return (
    <Card className="space-y-4">
      <h2 className="font-medium">Generate</h2>

      <div>
        <h3 className="text-sm font-medium">Pick a template</h3>
        <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {BUILT_IN_TEMPLATES.map((t) => {
            const active = template?.id === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTemplate(t)}
                className={`flex flex-col items-start gap-1 rounded border p-3 text-left text-sm transition-colors ${
                  active
                    ? 'border-primary bg-primary/10'
                    : 'border-border bg-background/40 hover:bg-muted/40'
                }`}
              >
                <div className="flex w-full items-center justify-between">
                  <span className="font-medium">{t.name}</span>
                  <span className="text-xs text-muted-foreground">{t.aspectRatio}</span>
                </div>
                <p className="text-xs leading-snug text-muted-foreground">{t.description}</p>
                {t.needsProductImages ? (
                  <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-300">
                    needs product image
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {template ? (
        <div className="space-y-4 border-t border-border pt-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="LLM (prompt fill)">
              <select
                className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm"
                value={llmModelId}
                onChange={(e) => setLlmModelId(e.target.value)}
              >
                {LLM_MODELS.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Image model">
              <select
                className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm"
                value={imageModelId}
                onChange={(e) => setImageModelId(e.target.value)}
              >
                {availableImageModels.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </select>
              {template.needsProductImages ? (
                <span className="text-xs text-muted-foreground">
                  Filtered to models that accept reference images.
                </span>
              ) : null}
            </Field>
            <Field label="Aspect ratio">
              <select
                className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm"
                value={aspectRatio}
                onChange={(e) => setAspectRatio(e.target.value as AspectRatio)}
              >
                {ASPECT_RATIOS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                    {r === template.aspectRatio ? ' (template default)' : ''}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Resolution">
              <select
                className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm"
                value={resolution}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (v === 1024 || v === 2048 || v === 4096) setResolution(v);
                }}
              >
                <option value={1024}>1K</option>
                <option value={2048}>2K</option>
                <option value={4096}>4K</option>
              </select>
            </Field>
            <Field label="Images to generate">
              <Input
                type="number"
                min={1}
                max={10}
                value={numImages}
                onChange={(e) => setNumImages(Math.max(1, Math.min(10, Number(e.target.value))))}
              />
            </Field>
          </div>

          <Field label="Concept / scene direction (optional)">
            <textarea
              rows={3}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40"
              placeholder="e.g. snowy alpine cabin at dusk, founder holding the bottle by a window"
              value={conceptBrief}
              onChange={(e) => setConceptBrief(e.target.value)}
            />
            <span className="text-xs text-muted-foreground">
              Steers headlines, scenes, and props for this batch. Leave empty to let the LLM choose freely from the brand DNA.
            </span>
          </Field>

          {template.needsProductImages ? (
            <div>
              <h3 className="text-sm font-medium">Product images for this run</h3>
              {productImages.length === 0 ? (
                <p className="mt-1 text-sm text-amber-300">
                  Upload at least one product image above before running this template.
                </p>
              ) : (
                <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
                  {previews.map((p) => {
                    const checked = selectedImageIds.has(p.id);
                    return (
                      <label
                        key={p.id}
                        className={`relative cursor-pointer rounded border ${
                          checked ? 'border-primary' : 'border-border opacity-60'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const next = new Set(selectedImageIds);
                            if (e.target.checked) next.add(p.id);
                            else next.delete(p.id);
                            setSelectedImageIds(next);
                          }}
                          className="absolute left-1 top-1 h-4 w-4"
                        />
                        <img
                          src={p.url}
                          alt={p.filename}
                          className="aspect-square w-full rounded object-cover"
                          loading="lazy"
                        />
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          ) : null}

          <div className="flex items-center justify-between rounded border border-border bg-background/40 p-3 text-sm">
            <span>
              Estimated cost:{' '}
              <strong>{estimate ? formatUsd(estimate.expected) : 'n/a'}</strong>{' '}
              {estimate ? (
                <span className="text-muted-foreground">
                  (range {formatUsd(estimate.low)} – {formatUsd(estimate.high)})
                </span>
              ) : null}
            </span>
            <Button
              onClick={() => void start()}
              disabled={!clientReady || Boolean(needsImagesButNoneSelected)}
            >
              Start generation
            </Button>
          </div>
          {!clientReady ? (
            <p className="text-xs text-amber-300">Set your Replicate token in Settings before starting.</p>
          ) : null}
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>
      ) : null}
    </Card>
  );
}

// ---------------- Results ----------------

function ResultsCard({
  brandId,
  jobs,
}: {
  brandId: string;
  jobs: readonly Job[];
}) {
  const roots = jobs.filter((j) => !j.parentJobId);
  const [expandedId, setExpandedId] = useState<string | null>(roots[0]?.id ?? null);

  if (roots.length === 0) {
    return (
      <Card>
        <h2 className="font-medium">Runs</h2>
        <p className="mt-2 text-sm text-muted-foreground">No runs yet for this brand.</p>
      </Card>
    );
  }

  return (
    <Card className="space-y-3">
      <h2 className="font-medium">Runs</h2>
      <ul className="space-y-1">
        {roots.slice(0, 20).map((j) => {
          const expanded = expandedId === j.id;
          return (
            <li key={j.id} className="rounded border border-border bg-background/40">
              <button
                type="button"
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm"
                onClick={() => setExpandedId(expanded ? null : j.id)}
              >
                <span className="truncate">{j.label}</span>
                <span className="text-xs text-muted-foreground">
                  {j.status} · {formatUsd(j.actualCostUsd)}
                </span>
              </button>
              {expanded ? <RunGallery brandId={brandId} jobId={j.id} /> : null}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function RunGallery({ jobId }: { brandId: string; jobId: string }) {
  const images = useLiveQuery(
    () =>
      getDB()
        .jobs.where('parentJobId')
        .equals(jobId)
        .and((j) => j.kind === 'image-generate')
        .toArray()
        .then(async (children) => {
          const all = await Promise.all(
            children.map((c) => getDB().generatedImages.where('jobId').equals(c.id).toArray()),
          );
          return all.flat();
        }),
    [jobId],
  );

  const previews = useMemo(
    () =>
      (images ?? []).map((g) => ({
        id: g.id,
        url: URL.createObjectURL(g.blob),
        label: g.templateName,
      })),
    [images],
  );
  useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)), [previews]);

  if (previews.length === 0) {
    return (
      <p className="border-t border-border px-3 py-2 text-xs text-muted-foreground">
        No images for this run yet.
      </p>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-2 border-t border-border p-3 sm:grid-cols-3 md:grid-cols-4">
      {previews.map((p) => (
        <img
          key={p.id}
          src={p.url}
          alt={p.label}
          className="w-full rounded"
          loading="lazy"
        />
      ))}
    </div>
  );
}

// ---------------- shared ----------------

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
